import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "./api";
import { useAuth } from "./AuthContext";
import { Icon } from "./Icons";
import { openPrintWindow, esc, num as pnum } from "./printUtils";

const DS = {
  dark:    "#1A1A1A",
  blue:    "#1565C0",
  grey:    "#546E7A",
  light:   "#F5F5F5",
  border:  "#E0E0E0",
  text:    "#212121",
  textSub: "#757575",
  error:   "#B71C1C",
  success: "#2E7D32",
};
const FONT          = "Roboto, 'Segoe UI', Arial, sans-serif";
const FONT_SIZE     = 15;
const FONT_TOTAL    = 22; // valori righe footer (totali/persone/giorni/capacità)
const FONT_LABEL    = 17; // etichette righe footer
const COL_INPUT     = "#ffffff";
const COL_READ      = "#F5F5F5";
const COL_VERDE_NEG = "#FFEBEE";
const WEEK_BORDER   = "3px solid #546E7A";
const MIN_COMMESSA  = 180;
const MIN_CNT       = 130;
const MIN_REP       = 215;
const MIN_RIEPILOGO = 92;
const W_DEL         = 36;
const STICKY_CNT_LEFT = MIN_COMMESSA + W_DEL; // offset sticky colonna Container (vista espansa)
const CNT_SHADOW    = "3px 0 6px -2px rgba(0,0,0,0.18)";

// ── Input nominali ─────────────────────────────────────────────────────────────
function NomInput({ value, onSave, disabled }) {
  const [local, setLocal]     = useState(String(value ?? 0));
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing) setLocal(String(value ?? 0));
  }, [value, editing]);

  return (
    <input
      type="number"
      value={local}
      disabled={disabled}
      onFocus={() => setEditing(true)}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => {
        setEditing(false);
        const v = parseFloat(local) || 0;
        if (v !== (value ?? 0)) onSave(v);
      }}
      onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
      style={{
        width: 72, border: "none", background: "transparent",
        textAlign: "center", fontSize: 30, fontWeight: 700,
        padding: "2px 2px", outline: "none", fontFamily: FONT,
        color: disabled ? "#BBBBBB" : DS.text,
        cursor: disabled ? "default" : "text",
      }}
    />
  );
}

// ── Cella reparto: layout esempio_cella.png ────────────────────────────────────
// nom (input grande) | ant/fab/ver (piccolo) | linea tenue | res
function RepCell({ rep, disabled, onNomSave }) {
  const nom = rep?.colli_nominali   ?? 0;
  const ant = rep?.colli_anticipati ?? 0;
  const fab = rep?.colli_fabbisogno ?? 0;
  const ver = rep?.colli_versati    ?? 0;
  const res = rep?.colli_residui    ?? 0;

  const SUB_COLOR = "#BBBBBB"; // colore attenuato per tutti i valori secondari

  return (
    <div style={{ display: "flex", alignItems: "stretch", minHeight: 72, height: "100%" }}>
      {/* Nominali — numero più grande, in evidenza */}
      <div style={{
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        padding: "2px 4px", flexShrink: 0, minWidth: 78,
        background: disabled ? COL_READ : COL_INPUT,
        borderRight: `1px solid ${DS.border}`,
      }}>
        <span style={{ fontSize: 11, color: SUB_COLOR, letterSpacing: 0.3 }}>nom.</span>
        <NomInput value={nom} onSave={onNomSave ?? (() => {})} disabled={disabled} />
      </div>
      {/* ant / fab / ver — attenuati */}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        justifyContent: "center", padding: "3px 8px", gap: 3,
        background: COL_READ, minWidth: 78,
      }}>
        <span style={{ fontSize: 14, color: SUB_COLOR, whiteSpace: "nowrap" }}>
          ant. <b>{ant.toLocaleString("it-IT")}</b>
        </span>
        <span style={{ fontSize: 14, color: SUB_COLOR, whiteSpace: "nowrap" }}>
          fab. <b>{fab.toLocaleString("it-IT")}</b>
        </span>
        <span style={{ fontSize: 14, color: SUB_COLOR, whiteSpace: "nowrap" }}>
          ver. <b>{ver.toLocaleString("it-IT")}</b>
        </span>
      </div>
      {/* Residui — separatore interno molto tenue */}
      <div style={{
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        borderLeft: `1px solid #D8D8D8`,
        padding: "2px 8px", flexShrink: 0, minWidth: 64,
        background: COL_READ,
      }}>
        <span style={{ fontSize: 11, color: SUB_COLOR, letterSpacing: 0.3 }}>res.</span>
        <span style={{
          fontSize: 24, fontWeight: 700,
          color: res < 0 ? "#EF9A9A" : (res === 0 ? "#A5D6A7" : SUB_COLOR),
        }}>
          {res.toLocaleString("it-IT")}
        </span>
      </div>
    </div>
  );
}

// ── Cella Riepilogo: somma nominali + somma residui (vista espansa) ────────────
function RiepilogoCell({ nom, res }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "4px 4px", minHeight: 72, height: "100%" }}>
      <span style={{ fontSize: 11, color: "#BBBBBB", letterSpacing: 0.3 }}>nom.</span>
      <span style={{ fontSize: 22, fontWeight: 700, color: DS.text, lineHeight: 1.2 }}>{nom.toLocaleString("it-IT")}</span>
      <span style={{ marginTop: 4, fontSize: 11, color: "#BBBBBB", letterSpacing: 0.3 }}>res.</span>
      <span style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, color: res < 0 ? "#EF9A9A" : (res === 0 ? "#A5D6A7" : "#BBBBBB") }}>
        {res.toLocaleString("it-IT")}
      </span>
    </div>
  );
}

// ── Input editabile generico ───────────────────────────────────────────────────
function EditableCell({ value, onSave, type = "text", style = {}, placeholder = "", readOnly = false }) {
  const [local, setLocal]     = useState(value ?? "");
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing) setLocal(value ?? "");
  }, [value, editing]);

  const handleBlur = () => {
    setEditing(false);
    const parsed = type === "number" ? (parseFloat(local) || 0) : local;
    if (parsed !== value) onSave(parsed);
  };

  return (
    <input
      type={type === "number" ? "number" : "text"}
      value={local}
      placeholder={placeholder}
      readOnly={readOnly}
      onFocus={() => { if (!readOnly) setEditing(true); }}
      onChange={e => { if (!readOnly) setLocal(e.target.value); }}
      onBlur={readOnly ? undefined : handleBlur}
      onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
      style={{
        width: "100%", border: "none", background: "transparent",
        textAlign: type === "number" ? "center" : "left",
        fontSize: FONT_SIZE, padding: "4px 6px", outline: "none",
        cursor: readOnly ? "default" : "text", fontFamily: FONT, color: DS.text,
        ...style,
      }}
    />
  );
}

// ── Header settimana ───────────────────────────────────────────────────────────
function WeekHeader({ w, idx, total, collapsed, editMode, onToggleCollapse, onMove, onDelete, onArchive }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4, padding: "0 4px" }}>
      {editMode ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <button onClick={() => onMove(w.id, "left")} disabled={idx === 0}
            style={{ ...styles.arrowBtn, opacity: idx === 0 ? 0.3 : 1 }} title="Sposta a sinistra"><Icon name="chevron_left" size={12} color="#fff" /></button>
          <button onClick={() => onMove(w.id, "right")} disabled={idx === total - 1}
            style={{ ...styles.arrowBtn, opacity: idx === total - 1 ? 0.3 : 1 }} title="Sposta a destra"><Icon name="chevron_right" size={12} color="#fff" /></button>
        </div>
      ) : <div style={{ width: 20 }} />}

      <span style={{ flex: 1, textAlign: "center", fontWeight: 700, fontSize: FONT_SIZE }}>
        {collapsed ? `W${pad2(w.numero)}` : w.nome}
      </span>

      <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <button onClick={() => onToggleCollapse(w.id)}
          style={{ ...styles.iconBtn, color: "#fff", borderColor: "rgba(255,255,255,0.4)" }}
          title={collapsed ? "Espandi" : "Collassa"}>
          {collapsed ? <Icon name="chevron_right" size={13} color="#fff" /> : <Icon name="chevron_left" size={13} color="#fff" />}
        </button>
        {editMode && (
          <button onClick={() => onArchive(w.id, w.nome)}
            style={{ ...styles.iconBtn, color: "#fff", borderColor: "rgba(255,255,255,0.4)" }}
            title="Archivia settimana"><Icon name="archive" size={13} color="#fff" /></button>
        )}
        {editMode && (
          <button onClick={() => onDelete(w.id, w.nome)}
            style={{ ...styles.iconBtn, color: "#ffcdd2", borderColor: "rgba(255,255,255,0.4)" }}
            title="Elimina settimana">×</button>
        )}
      </div>
    </div>
  );
}

// ── Costruzione HTML per la stampa ────────────────────────────────────────────

// Totali di una settimana (per la stampa): breakdown per reparto + righe scalari,
// stessa logica delle righe footer a schermo.
function weekTotals(tabella, weekId) {
  const { rows = [], reparti = [], weeks = [] } = tabella || {};
  const w = weeks.find(x => x.id === weekId) || {};
  const rep = {};
  reparti.forEach(r => { rep[r.id] = { nom: 0, ant: 0, fab: 0, ver: 0, res: 0 }; });
  rows.forEach(row => {
    if (row.commessa.conta_carico === 0) return; // esclusa dal carico
    (row.weeks[String(weekId)] || []).forEach(cell => {
      (cell.reparti || []).forEach(rd => {
        const t = rep[rd.reparto_id];
        if (!t) return;
        t.nom += rd.colli_nominali   || 0;
        t.ant += rd.colli_anticipati || 0;
        t.fab += rd.colli_fabbisogno || 0;
        t.ver += rd.colli_versati    || 0;
        t.res += rd.colli_residui    || 0;
      });
    });
  });
  const totNom  = reparti.reduce((s, r) => s + rep[r.id].nom, 0);
  const totRes  = reparti.reduce((s, r) => s + rep[r.id].res, 0);
  const cpp     = w.colli_per_settimana_persona ?? 235;
  const persone = w.numero_persone ?? 0;
  const gg      = w.giorni_residui ?? 5;
  const capInst = gg * persone * (cpp / 5);
  const capRes  = capInst - totRes;
  const rateGg  = cpp > 0 && gg > 0 ? (cpp / 5) * gg : 0;
  const teste   = rateGg > 0 ? capRes / rateGg : 0;
  return { rep, totNom, totRes, cpp, persone, gg, capInst, capRes, teste, rateGg };
}

// IDs delle settimane comprese tra due estremi (inclusi), nell'ordine di `weeks`.
function weekRange(weeks, aId, bId) {
  const ia = weeks.findIndex(w => w.id === aId);
  const ib = weeks.findIndex(w => w.id === bId);
  if (ia < 0 || ib < 0) return [];
  const [lo, hi] = ia <= ib ? [ia, ib] : [ib, ia];
  return weeks.slice(lo, hi + 1).map(w => w.id);
}

// Stampa GLOBALE compressa: righe = commesse, colonne = settimane (nom / res sommati).
function compressaHtml(tabella, weekIds) {
  const { weeks = [], rows = [] } = tabella || {};
  const selWeeks = weeks.filter(w => weekIds.includes(w.id));
  const head = `<tr><th class="lbl">Commessa</th>${selWeeks.map(w => `<th>${esc(w.nome)}</th>`).join("")}</tr>`;
  const body = rows.map(row => {
    const cells = selWeeks.map(w => {
      const cs = row.weeks[String(w.id)] || [];
      if (cs.length === 0) return `<td></td>`;
      const nom = cs.reduce((s, c) => s + (c.colli_nominali ?? 0), 0);
      const res = cs.reduce((s, c) => s + (c.colli_residui ?? 0), 0);
      return `<td>nom. <b>${pnum(nom)}</b><br/>res. <b class="${res <= 0 ? "neg" : ""}">${pnum(res)}</b></td>`;
    }).join("");
    const escl = row.commessa.conta_carico === 0 ? " (esclusa)" : "";
    return `<tr><td class="lbl">${esc(row.commessa.codice)}${escl}</td>${cells}</tr>`;
  }).join("");

  // Righe totali (una colonna per settimana)
  const T = selWeeks.map(w => weekTotals(tabella, w.id));
  const totRow = (label, cellFn, first) =>
    `<tr class="totrow${first ? " totrow-first" : ""}"><td class="lbl">${esc(label)}</td>${T.map(cellFn).join("")}</tr>`;
  const totali =
    totRow("Totali (nom / res)", t => `<td>${pnum(t.totNom)} / <span class="${t.totRes <= 0 ? "neg" : ""}">${pnum(t.totRes)}</span></td>`, true)
    + totRow("Numero persone",       t => `<td>${pnum(t.persone)}</td>`)
    + totRow("Colli/sett./pers.",    t => `<td>${pnum(t.cpp)}</td>`)
    + totRow("Giorni residui",       t => `<td>${pnum(t.gg)}</td>`)
    + totRow("Capacità installata",  t => `<td>${pnum(Math.round(t.capInst))}</td>`)
    + totRow("Capacità residua",     t => `<td class="${t.capRes < 0 ? "err" : "neg"}">${pnum(Math.round(t.capRes))}${t.rateGg > 0 ? ` <small>(${t.teste.toFixed(1)} t.)</small>` : ""}</td>`);

  return `<table><thead>${head}</thead><tbody>${body}${totali}</tbody></table>`;
}

// Stampa SINGOLA settimana espansa: colonne = reparti con nom/ant/fab/ver/res.
function espansaHtml(tabella, weekId) {
  const { rows = [], reparti = [] } = tabella || {};
  const head = `<tr><th class="lbl">Commessa</th><th class="lbl">Container</th><th class="tot">Riepilogo</th>${reparti.map(r => `<th>${esc(r.nome)}</th>`).join("")}</tr>`;
  const bodyRows = [];
  rows.forEach(row => {
    const cells = row.weeks[String(weekId)] || [];
    if (cells.length === 0) return;
    cells.forEach((cell, ci) => {
      const rie = `nom. <b>${pnum(cell.colli_nominali ?? 0)}</b><br/>res. <b>${pnum(cell.colli_residui ?? 0)}</b>`;
      const repCells = reparti.map(rep => {
        const rd = (cell.reparti || []).find(r => r.reparto_id === rep.id) || {};
        return `<td>nom. <b>${pnum(rd.colli_nominali ?? 0)}</b><br/>`
          + `<small>ant. ${pnum(rd.colli_anticipati ?? 0)} · fab. ${pnum(rd.colli_fabbisogno ?? 0)} · ver. ${pnum(rd.colli_versati ?? 0)}</small><br/>`
          + `res. <b class="${(rd.colli_residui ?? 0) <= 0 ? "neg" : ""}">${pnum(rd.colli_residui ?? 0)}</b></td>`;
      }).join("");
      const commessaCell = ci === 0
        ? `<td class="lbl" rowspan="${cells.length}">${esc(row.commessa.codice)}</td>`
        : "";
      bodyRows.push(`<tr>${commessaCell}<td class="lbl">${esc(cell.container_schedulato || "—")}</td><td class="tot">${rie}</td>${repCells}</tr>`);
    });
  });

  // Righe totali della settimana
  const t = weekTotals(tabella, weekId);
  const repTot = reparti.map(rep => {
    const r = t.rep[rep.id] || {};
    return `<td>nom. <b>${pnum(r.nom || 0)}</b><br/>`
      + `<small>ant. ${pnum(r.ant || 0)} · fab. ${pnum(r.fab || 0)} · ver. ${pnum(r.ver || 0)}</small><br/>`
      + `res. <b class="${(r.res || 0) <= 0 ? "neg" : ""}">${pnum(r.res || 0)}</b></td>`;
  }).join("");
  const nRep = reparti.length;
  const scalarRow = (label, valHtml) =>
    `<tr class="totrow"><td class="lbl" colspan="2">${esc(label)}</td><td class="tot">${valHtml}</td><td colspan="${nRep}"></td></tr>`;
  const totali =
    `<tr class="totrow totrow-first"><td class="lbl" colspan="2">Totali</td><td class="tot">nom. <b>${pnum(t.totNom)}</b><br/>res. <b>${pnum(t.totRes)}</b></td>${repTot}</tr>`
    + scalarRow("Numero persone",      pnum(t.persone))
    + scalarRow("Colli/sett./pers.",   pnum(t.cpp))
    + scalarRow("Giorni residui",      pnum(t.gg))
    + scalarRow("Capacità installata", pnum(Math.round(t.capInst)))
    + scalarRow("Capacità residua",    `<span class="${t.capRes < 0 ? "err" : "neg"}">${pnum(Math.round(t.capRes))}</span>${t.rateGg > 0 ? ` <small>(${t.teste.toFixed(1)} teste)</small>` : ""}`);

  return `<table><thead>${head}</thead><tbody>${bodyRows.join("")}${totali}</tbody></table>`;
}

// ── Modale selezione settimane per la stampa ──────────────────────────────────
function ModaleStampa({ mode, weeks, onClose, onConfirm }) {
  const [daId, setDaId] = useState(weeks[0]?.id ?? null);
  const [aId, setAId]   = useState(weeks[weeks.length - 1]?.id ?? null);
  const [singolaId, setSingolaId] = useState(weeks[0]?.id ?? null);

  const isGlobale = mode === "globale";
  return (
    <div style={styles.overlay}>
      <div style={styles.modale}>
        <h3 style={{ margin: "0 0 16px", color: "#2d3748" }}>
          {isGlobale ? "Stampa globale (compressa)" : "Stampa singola settimana (espansa)"}
        </h3>
        {isGlobale ? (
          <>
            <label style={styles.label}>Dalla settimana</label>
            <select style={styles.input} value={daId ?? ""} onChange={e => setDaId(parseInt(e.target.value))}>
              {weeks.map(w => <option key={w.id} value={w.id}>{w.nome}</option>)}
            </select>
            <label style={styles.label}>Alla settimana</label>
            <select style={styles.input} value={aId ?? ""} onChange={e => setAId(parseInt(e.target.value))}>
              {weeks.map(w => <option key={w.id} value={w.id}>{w.nome}</option>)}
            </select>
          </>
        ) : (
          <>
            <label style={styles.label}>Settimana</label>
            <select style={styles.input} value={singolaId ?? ""} onChange={e => setSingolaId(parseInt(e.target.value))}>
              {weeks.map(w => <option key={w.id} value={w.id}>{w.nome}</option>)}
            </select>
          </>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <button onClick={onClose} style={styles.btnSecondary}>Annulla</button>
          <button
            onClick={() => onConfirm(isGlobale ? { mode, daId, aId } : { mode, weekId: singolaId })}
            style={styles.btnPrimary}
          >
            <Icon name="print" size={15} /> Stampa
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Settimane: utilità anno / numero ISO ──────────────────────────────────────
const pad2 = n => String(n).padStart(2, "0");

// Anno ISO e numero di settimana ISO di una data.
function isoWeekInfo(date) {
  const d   = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const anno = d.getUTCFullYear();
  const y    = new Date(Date.UTC(anno, 0, 1));
  return { anno, numero: Math.ceil((((d - y) / 86400000) + 1) / 7) };
}

// Settimane ISO in un anno (52 o 53): è il numero di settimana del 28 dicembre.
const settimaneNellAnno = anno => isoWeekInfo(new Date(anno, 11, 28)).numero;

// Settimana successiva all'ultima presente (per anno/numero); se non ce ne sono, quella corrente.
function prossimaSettimana(weeks) {
  if (!weeks || !weeks.length) return isoWeekInfo(new Date());
  const ultima = weeks.reduce((a, w) =>
    (w.anno > a.anno || (w.anno === a.anno && w.numero > a.numero)) ? w : a, weeks[0]);
  return ultima.numero >= settimaneNellAnno(ultima.anno)
    ? { anno: ultima.anno + 1, numero: 1 }
    : { anno: ultima.anno, numero: ultima.numero + 1 };
}

// ── Modale settimana ───────────────────────────────────────────────────────────
function ModaleWeek({ onClose, onSave, weeks }) {
  const iniziale = prossimaSettimana(weeks);
  const [anno, setAnno]         = useState(iniziale.anno);
  const [numero, setNumero]     = useState(iniziale.numero);
  const [capacita, setCapacita] = useState(2500);
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState("");

  const a = parseInt(anno), n = parseInt(numero);
  const anteprima = (a && n) ? `W${pad2(n)} ${a}` : "—";

  const submit = async () => {
    if (!a || a < 2000 || a > 2100) { setErr("Anno non valido"); return; }
    if (!n || n < 1 || n > 53) { setErr("Numero settimana non valido (1-53)"); return; }
    if ((weeks || []).some(w => w.anno === a && w.numero === n)) { setErr(`La settimana ${n} del ${a} esiste già`); return; }
    setLoading(true);
    try {
      await onSave({ anno: a, numero: n, capacita_installata: capacita });
      onClose();
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modale}>
        <h3 style={{ margin: "0 0 16px", color: "#2d3748" }}>Aggiungi Settimana</h3>
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={styles.label}>Anno</label>
            <input style={styles.input} type="number" min={2000} max={2100} value={anno}
              onChange={e => setAnno(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={styles.label}>Settimana (n° ISO)</label>
            <input style={styles.input} type="number" min={1} max={53} value={numero}
              onChange={e => setNumero(e.target.value)} />
          </div>
        </div>
        <div style={{ fontSize: 13, color: "#718096", margin: "-4px 0 12px" }}>
          Verrà creata la colonna <strong>{anteprima}</strong>
        </div>
        <label style={styles.label}>Capacità installata (colli)</label>
        <input style={styles.input} type="number" value={capacita} onChange={e => setCapacita(parseFloat(e.target.value) || 0)} />
        {err && <div style={{ color: "#e53e3e", fontSize: 13, marginBottom: 8 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={styles.btnSecondary}>Annulla</button>
          <button onClick={submit} disabled={loading} style={styles.btnPrimary}>{loading ? "..." : "Salva"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Modale commessa ────────────────────────────────────────────────────────────
function ModaleCommessa({ onClose, onSave }) {
  const [codice, setCodice]   = useState("");
  const [desc, setDesc]       = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState("");

  const submit = async () => {
    if (!codice.trim()) { setErr("Codice obbligatorio"); return; }
    setLoading(true);
    try {
      await onSave({ codice: codice.trim(), descrizione: desc.trim() || null });
      onClose();
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modale}>
        <h3 style={{ margin: "0 0 16px", color: "#2d3748" }}>Aggiungi Commessa</h3>
        <label style={styles.label}>Codice commessa *</label>
        <input style={styles.input} value={codice} onChange={e => setCodice(e.target.value)} placeholder="es. CO-231" />
        <label style={styles.label}>Descrizione</label>
        <input style={styles.input} value={desc} onChange={e => setDesc(e.target.value)} placeholder="Opzionale" />
        {err && <div style={{ color: "#e53e3e", fontSize: 13, marginBottom: 8 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={styles.btnSecondary}>Annulla</button>
          <button onClick={submit} disabled={loading} style={styles.btnPrimary}>{loading ? "..." : "Salva"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Componente principale ──────────────────────────────────────────────────────
export default function TabellaPianificazione() {
  const { canEdit } = useAuth();
  const puoModificare = canEdit("settimanale");
  const [tabella, setTabella]               = useState(null);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState("");
  const [showModaleWeek, setShowModaleWeek] = useState(false);
  const [showModaleCommessa, setShowModaleCommessa] = useState(false);
  const [collapsedWeeks, setCollapsedWeeks] = useState({});
  const [editMode, setEditMode]             = useState(false);
  const [hoveredAdd, setHoveredAdd]         = useState(null);
  const [spostaMenu, setSpostaMenu]         = useState(null); // { commessaId, container, fromWeekId }
  const [soloTotali, setSoloTotali]         = useState(false);
  const [modaleStampa, setModaleStampa]     = useState(null); // 'globale' | 'singola' | null
  const saveTimer           = useRef({});
  const pendingPayload      = useRef({});
  const hasInitCollapse     = useRef(false);
  const head1Ref            = useRef(null);
  const [head1H, setHead1H] = useState(48); // altezza riga header settimane (per offset sticky riga 2)

  const loadTabella = useCallback(async () => {
    try {
      const data = await api.getTabella();
      setTabella(data);
      setError("");
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTabella(); }, [loadTabella]);

  // Misura l'altezza della riga header settimane per ancorare la riga 2 subito sotto
  useEffect(() => {
    const el = head1Ref.current;
    if (!el) return;
    const setH = () => setHead1H(el.offsetHeight);
    setH();
    const ro = new ResizeObserver(setH);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tabella, collapsedWeeks, editMode]);

  // Al primo caricamento: espandi solo la settimana corrente, collassa le altre
  useEffect(() => {
    if (hasInitCollapse.current || !tabella?.weeks?.length) return;
    hasInitCollapse.current = true;
    const oggi  = isoWeekInfo(new Date());
    const match = tabella.weeks.find(w => w.anno === oggi.anno && w.numero === oggi.numero);
    if (match) {
      const next = {};
      tabella.weeks.forEach(w => { next[w.id] = w.id !== match.id; });
      setCollapsedWeeks(next);
    }
  }, [tabella]);

  const scheduleSave = (key, payload) => {
    pendingPayload.current[key] = payload;
    if (saveTimer.current[key]) clearTimeout(saveTimer.current[key]);
    saveTimer.current[key] = setTimeout(async () => {
      const p = pendingPayload.current[key];
      if (!p) return;
      try {
        const result = await api.upsertPianificazione(p);
        setTabella(prev => {
          if (!prev) return prev;
          const rows = prev.rows.map(row => {
            if (row.commessa.id !== p.commessa_id) return row;
            const newWeeks = { ...row.weeks };
            const cells = [...(newWeeks[String(p.week_id)] || [])];
            const idx = cells.findIndex(c =>
              (c.piano_id && c.piano_id === result.id) ||
              (c.container_schedulato === p.container_schedulato)
            );
            if (idx >= 0) {
              cells[idx] = {
                ...cells[idx],
                piano_id:        result.id              || cells[idx].piano_id,
                colli_anticipati: result.colli_anticipati ?? cells[idx].colli_anticipati,
                colli_versati:    result.colli_versati    ?? cells[idx].colli_versati,
              };
            }
            newWeeks[String(p.week_id)] = cells;
            return { ...row, weeks: newWeeks };
          });
          return { ...prev, rows };
        });
        setError("");
      } catch (e) {
        setError("Errore salvataggio: " + e.message);
        await loadTabella();
      }
    }, 500);
  };

  const handleContainerChange = (commessaId, weekId, cellIdx, value) => {
    setTabella(prev => {
      const rows = prev.rows.map(r => {
        if (r.commessa.id !== commessaId) return r;
        const newWeeks = { ...r.weeks };
        const cs = [...(newWeeks[String(weekId)] || [{}])];
        cs[cellIdx] = { ...(cs[cellIdx] || {}), container_schedulato: value };
        newWeeks[String(weekId)] = cs;
        return { ...r, weeks: newWeeks };
      });
      return { ...prev, rows };
    });
    scheduleSave(`${commessaId}_${weekId}_${cellIdx}`, {
      commessa_id: commessaId,
      week_id: weekId,
      container_schedulato: value,
    });
  };

  const handleNominaliChange = (commessaId, weekId, cellIdx, repartoId, value) => {
    const row = tabella?.rows.find(r => r.commessa.id === commessaId);
    const cells = row?.weeks[String(weekId)] || [{}];
    const cell = cells[cellIdx] || {};
    const container = cell.container_schedulato;
    if (!cell.piano_id || !container) return;

    setTabella(prev => {
      const rows = prev.rows.map(r => {
        if (r.commessa.id !== commessaId) return r;
        const newWeeks = { ...r.weeks };
        const cs = [...(newWeeks[String(weekId)] || [{}])];
        const c = { ...(cs[cellIdx] || {}) };
        c.reparti = (c.reparti || []).map(rep => {
          if (rep.reparto_id !== repartoId) return rep;
          const newNom = value;
          const newFab = newNom - (rep.colli_anticipati || 0);
          return { ...rep, colli_nominali: newNom, colli_fabbisogno: newFab, colli_residui: newFab - (rep.colli_versati || 0) };
        });
        c.colli_nominali       = c.reparti.reduce((s, rep) => s + (rep.colli_nominali || 0), 0);
        c.colli_da_pianificare = c.colli_nominali - (c.colli_anticipati || 0);
        c.colli_residui        = c.colli_da_pianificare - (c.colli_versati || 0);
        cs[cellIdx] = c;
        newWeeks[String(weekId)] = cs;
        return { ...r, weeks: newWeeks };
      });
      return { ...prev, rows };
    });

    const key = `nom_${commessaId}_${weekId}_${container}_${repartoId}`;
    if (saveTimer.current[key]) clearTimeout(saveTimer.current[key]);
    saveTimer.current[key] = setTimeout(async () => {
      try {
        await api.upsertNominaliReparto({ commessa_id: commessaId, week_id: weekId, container, reparto_id: repartoId, colli_nominali: value });
        setError("");
      } catch (e) {
        setError("Errore salvataggio nominali: " + e.message);
        await loadTabella();
      }
    }, 500);
  };

  const handleAddContainer = (commessaId, weekId) => {
    setTabella(prev => {
      const rows = prev.rows.map(row => {
        if (row.commessa.id !== commessaId) return row;
        const newWeeks = { ...row.weeks };
        const cells = [...(newWeeks[String(weekId)] || [])];
        cells.push({
          reparti: (prev.reparti || []).map(r => ({
            reparto_id: r.id, reparto_nome: r.nome,
            colli_nominali: 0, colli_anticipati: 0,
            colli_fabbisogno: 0, colli_versati: 0, colli_residui: 0,
          })),
        });
        newWeeks[String(weekId)] = cells;
        return { ...row, weeks: newWeeks };
      });
      return { ...prev, rows };
    });
  };

  const handleDeleteContainer = async (commessaId, weekId, cellIdx) => {
    const row = tabella?.rows.find(r => r.commessa.id === commessaId);
    const cells = row?.weeks[String(weekId)] || [];
    const cell = cells[cellIdx];
    if (cell?.piano_id) {
      try { await api.deletePianificazione(cell.piano_id); }
      catch (e) { setError("Errore eliminazione: " + e.message); return; }
    }
    setTabella(prev => {
      const rows = prev.rows.map(r => {
        if (r.commessa.id !== commessaId) return r;
        const newWeeks = { ...r.weeks };
        const newCells = [...(newWeeks[String(weekId)] || [])];
        newCells.splice(cellIdx, 1);
        if (newCells.length === 0) {
          newCells.push({ reparti: (prev.reparti || []).map(rep => ({
            reparto_id: rep.id, reparto_nome: rep.nome,
            colli_nominali: 0, colli_anticipati: 0,
            colli_fabbisogno: 0, colli_versati: 0, colli_residui: 0,
          })) });
        }
        newWeeks[String(weekId)] = newCells;
        return { ...r, weeks: newWeeks };
      });
      return { ...prev, rows };
    });
    await loadTabella();
  };

  const handleSpostaContainer = async (commessaId, container, fromWeekId, toWeekId) => {
    setSpostaMenu(null);
    try {
      await api.spostaContainer({ commessa_id: commessaId, container, from_week_id: fromWeekId, to_week_id: toWeekId });
      await loadTabella();
    } catch (e) {
      setError("Errore spostamento: " + e.message);
    }
  };

  const handleNumPersoneChange = async (weekId, value) => {
    try { await api.updateWeek(weekId, { numero_persone: value }); await loadTabella(); }
    catch (e) { setError("Errore aggiornamento persone: " + e.message); }
  };

  const handleGiorniResiduiChange = async (weekId, value) => {
    try { await api.updateWeek(weekId, { giorni_residui: value }); await loadTabella(); }
    catch (e) { setError("Errore aggiornamento giorni: " + e.message); }
  };

  const handleColliPpChange = async (weekId, value) => {
    try { await api.updateWeek(weekId, { colli_per_settimana_persona: value }); await loadTabella(); }
    catch (e) { setError(e.message); }
  };

  const handleMoveWeek = async (weekId, direction) => {
    const weeks = [...(tabella?.weeks || [])];
    const idx = weeks.findIndex(w => w.id === weekId);
    const swapIdx = direction === "left" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= weeks.length) return;
    [weeks[idx], weeks[swapIdx]] = [weeks[swapIdx], weeks[idx]];
    try {
      await Promise.all(weeks.map((w, i) => api.updateWeek(w.id, { ordine: i })));
      await loadTabella();
    } catch (e) { setError("Errore riordino: " + e.message); }
  };

  const handleToggleContaCarico = async (commessaId, attuale) => {
    try {
      await api.patchCommessa(commessaId, { conta_carico: attuale === 0 ? 1 : 0 });
      await loadTabella();
    } catch (e) { setError("Errore aggiornamento commessa: " + e.message); }
  };

  const handleArchiviaCommessa = async (row) => {
    const residuiAperti = Object.values(row.weeks || {}).some(cells =>
      (cells || []).some(c => (c.colli_residui ?? 0) > 0)
    );
    if (residuiAperti && !window.confirm(`La commessa ${row.commessa.codice} ha ancora fabbisogni aperti. Archiviare comunque?`)) return;
    try {
      await api.patchCommessa(row.commessa.id, { archiviata: 1 });
      await loadTabella();
    } catch (e) { setError("Errore archiviazione: " + e.message); }
  };

  const handleArchiviaWeek = async (weekId, nome) => {
    const totRes = tabella?.totali?.[String(weekId)]?.tot_residui ?? 0;
    if (totRes > 0 && !window.confirm(`La settimana ${nome} ha ancora fabbisogni aperti (${totRes.toLocaleString("it-IT")} colli residui). Archiviare comunque?`)) return;
    try {
      await api.updateWeek(weekId, { archiviata: 1 });
      await loadTabella();
    } catch (e) { setError("Errore archiviazione settimana: " + e.message); }
  };

  const handleAddWeek     = async (data) => { await api.createWeek(data); await loadTabella(); };
  const handleAddCommessa = async (data) => { await api.createCommessa(data); await loadTabella(); };
  const handleDeleteCommessa = async (id, codice) => {
    if (!window.confirm(`Eliminare la commessa ${codice}? Verranno eliminati anche tutti i dati di pianificazione.`)) return;
    try { await api.deleteCommessa(id); await loadTabella(); } catch (e) { setError(e.message); }
  };
  const handleDeleteWeek = async (id, nome) => {
    if (!window.confirm(`Eliminare la settimana ${nome}?`)) return;
    try { await api.deleteWeek(id); await loadTabella(); } catch (e) { setError(e.message); }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  if (loading) return <div style={styles.loading}>Caricamento...</div>;

  const { weeks = [], rows = [], totali = {}, reparti = [] } = tabella || {};

  const handleConfirmStampa = (payload) => {
    if (payload.mode === "globale") {
      const ids = weekRange(weeks, payload.daId, payload.aId);
      const da = weeks.find(w => w.id === payload.daId)?.nome || "";
      const a  = weeks.find(w => w.id === payload.aId)?.nome || "";
      openPrintWindow(`Pianificazione settimanale · ${da} → ${a}`, compressaHtml(tabella, ids));
    } else {
      const nome = weeks.find(w => w.id === payload.weekId)?.nome || "";
      openPrintWindow(`Pianificazione settimanale · ${nome}`, espansaHtml(tabella, payload.weekId));
    }
    setModaleStampa(null);
  };

  // NCOLS_EXPANDED: container + riepilogo + una colonna per ogni reparto
  const NCOLS_EXPANDED = 2 + reparti.length;

  // Totali per colonna-reparto per settimana (per la riga Totali)
  const weekRepTotals = {};
  weeks.forEach(w => {
    weekRepTotals[w.id] = {};
    reparti.forEach(r => { weekRepTotals[w.id][r.id] = { nom: 0, ant: 0, fab: 0, ver: 0, res: 0 }; });
  });
  rows.forEach(row => {
    if (row.commessa.conta_carico === 0) return; // disattivata: esclusa dal carico
    weeks.forEach(w => {
      (row.weeks[String(w.id)] || []).forEach(cell => {
        (cell.reparti || []).forEach(rd => {
          const wt = weekRepTotals[w.id];
          if (wt && wt[rd.reparto_id]) {
            wt[rd.reparto_id].nom += rd.colli_nominali   || 0;
            wt[rd.reparto_id].ant += rd.colli_anticipati || 0;
            wt[rd.reparto_id].fab += rd.colli_fabbisogno || 0;
            wt[rd.reparto_id].ver += rd.colli_versati    || 0;
            wt[rd.reparto_id].res += rd.colli_residui    || 0;
          }
        });
      });
    });
  });

  // Numero colonne totali per la riga vuota
  const totalCols = 2 + weeks.reduce((s, w) => s + (collapsedWeeks[w.id] ? 1 : NCOLS_EXPANDED), 0);

  return (
    <div style={{ fontFamily: FONT, background: DS.light, minHeight: "100vh" }}>
      <div style={{ padding: 20 }}>

        {error && (
          <div style={styles.errorBanner}>
            <Icon name="warning" size={16} color={DS.error} style={{ marginRight: 6 }} />{error}
            <button onClick={() => setError("")} style={{ marginLeft: "auto", cursor: "pointer", background: "none", border: "none", fontWeight: 700, fontSize: 18, color: DS.error }}>×</button>
          </div>
        )}

        {/* Toolbar */}
        <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap", background: "#fff", borderRadius: 8, padding: "12px 16px", border: `1px solid ${DS.border}` }}>
          {editMode && (
            <>
              <button onClick={() => setShowModaleCommessa(true)} style={styles.btnPrimary}>+ Commessa</button>
              <button onClick={() => setShowModaleWeek(true)} style={{ ...styles.btnPrimary, background: DS.dark }}>+ Settimana</button>
              <div style={{ width: 1, background: DS.border, alignSelf: "stretch", margin: "0 4px" }} />
            </>
          )}
          <button
            onClick={() => {
              const allCollapsed = weeks.every(w => collapsedWeeks[w.id]);
              const next = {};
              weeks.forEach(w => { next[w.id] = !allCollapsed; });
              setCollapsedWeeks(next);
            }}
            style={{ ...styles.btnPrimary, background: DS.grey }}
          >
            {weeks.every(w => collapsedWeeks[w.id]) ? "⊞ Espandi tutto" : "⊟ Comprimi tutto"}
          </button>
          <button
            onClick={() => setSoloTotali(v => !v)}
            style={{ ...styles.btnPrimary, background: soloTotali ? DS.dark : DS.grey }}
          >
            {soloTotali ? <><Icon name="grid" size={14} /> Mostra commesse</> : <><Icon name="functions" size={14} /> Solo totali</>}
          </button>
          <div style={{ width: 1, background: DS.border, alignSelf: "stretch", margin: "0 4px" }} />
          <button
            onClick={() => setModaleStampa("globale")}
            disabled={weeks.length === 0}
            title="Stampa tutte le settimane in un intervallo, in modalità compressa"
            style={{ ...styles.btnPrimary, background: DS.grey }}
          >
            <Icon name="print" size={15} /> Stampa globale
          </button>
          <button
            onClick={() => setModaleStampa("singola")}
            disabled={weeks.length === 0}
            title="Stampa una singola settimana in modalità espansa (tutti i reparti)"
            style={{ ...styles.btnPrimary, background: DS.grey }}
          >
            <Icon name="print" size={15} /> Stampa settimana
          </button>
          {puoModificare && (
          <button
            onClick={() => setEditMode(v => !v)}
            style={{ ...styles.btnPrimary, background: editMode ? DS.error : DS.blue, display: "flex", alignItems: "center", gap: 6 }}
          >
            {editMode ? <><Icon name="lock_open" size={16} color="#fff" /> Esci da modifica</> : <><Icon name="edit" size={16} color="#fff" /> Modalità modifica</>}
          </button>
          )}
        </div>

        <div style={{ overflow: "auto", maxHeight: "calc(100vh - var(--app-header-h, 150px) - 96px)" }}>
          <table style={styles.table}>
            <colgroup>
              <col style={{ minWidth: MIN_COMMESSA }} />
              <col style={{ width: W_DEL }} />
              {weeks.map(w => {
                const collapsed = !!collapsedWeeks[w.id];
                if (collapsed) return <col key={w.id} style={{ width: 92 }} />;
                return (
                  <React.Fragment key={w.id}>
                    <col style={{ minWidth: MIN_CNT }} />
                    <col style={{ minWidth: MIN_RIEPILOGO }} />
                    {reparti.map(r => <col key={r.id} style={{ minWidth: MIN_REP }} />)}
                  </React.Fragment>
                );
              })}
            </colgroup>

            <thead>
              {/* Riga 1: nomi settimane */}
              <tr ref={head1Ref}>
                <th style={{ ...styles.th, background: COL_READ, position: "sticky", left: 0, top: 0, zIndex: 7 }} colSpan={2} />
                {weeks.map((w, wi) => {
                  const collapsed = !!collapsedWeeks[w.id];
                  return (
                    <th key={w.id}
                      colSpan={collapsed ? 1 : NCOLS_EXPANDED}
                      style={{ ...styles.th, background: "#546E7A", color: "#fff", minWidth: collapsed ? 52 : undefined, padding: "6px 4px", borderLeft: WEEK_BORDER, position: "sticky", top: 0, zIndex: 6 }}
                    >
                      <WeekHeader
                        w={w} idx={wi} total={weeks.length} collapsed={collapsed}
                        editMode={editMode}
                        onToggleCollapse={id => setCollapsedWeeks(p => ({ ...p, [id]: !p[id] }))}
                        onMove={handleMoveWeek}
                        onDelete={handleDeleteWeek}
                        onArchive={handleArchiviaWeek}
                      />
                    </th>
                  );
                })}
              </tr>

              {/* Riga 2: sotto-intestazioni (Container + Reparti per settimana espansa) */}
              <tr>
                <th style={{ ...styles.th, background: COL_READ, fontSize: FONT_SIZE, minWidth: MIN_COMMESSA, whiteSpace: "nowrap", textAlign: "left", position: "sticky", left: 0, top: head1H, zIndex: 7 }}>Commessa</th>
                <th style={{ ...styles.th, background: COL_READ, position: "sticky", left: MIN_COMMESSA, top: head1H, zIndex: 7, boxShadow: "3px 0 6px -2px rgba(0,0,0,0.18)" }} />
                {weeks.map(w => {
                  const collapsed = !!collapsedWeeks[w.id];
                  if (collapsed) return <th key={w.id} style={{ ...styles.th, background: COL_READ, borderLeft: WEEK_BORDER, position: "sticky", top: head1H, zIndex: 6 }} />;
                  return (
                    <React.Fragment key={w.id}>
                      <th style={{ ...styles.th, background: COL_READ, fontSize: 13, whiteSpace: "nowrap", borderLeft: WEEK_BORDER, position: "sticky", left: STICKY_CNT_LEFT, top: head1H, zIndex: 7, boxShadow: CNT_SHADOW }}>Container</th>
                      <th style={{ ...styles.th, background: COL_READ, fontSize: 13, whiteSpace: "nowrap", position: "sticky", top: head1H, zIndex: 6 }}>Riepilogo</th>
                      {reparti.map(r => (
                        <th key={r.id} style={{ ...styles.th, background: "#78909C", color: "#fff", fontSize: 13, borderLeft: "2px solid #546E7A", position: "sticky", top: head1H, zIndex: 6 }}>{r.nome}</th>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {!soloTotali && rows.map((row, ri) => {
                const maxCells = Math.max(1, ...weeks.map(w => (row.weeks[String(w.id)] || [{}]).length));
                const rowBg    = ri % 2 === 1 ? "#F8F8F8" : "#fff";

                const disattivata = row.commessa.conta_carico === 0;

                return Array.from({ length: maxCells }).map((_, subRow) => {
                  const isFirst = subRow === 0;
                  const isLast  = subRow === maxCells - 1;
                  const cellBg  = disattivata ? "#EEEEEE" : rowBg;

                  return (
                    <tr key={`${row.commessa.id}_${subRow}`} style={{ background: cellBg }}>

                      {/* Commessa (rowSpan, sticky) */}
                      {isFirst && (
                        <td rowSpan={maxCells} style={{ ...styles.td, fontWeight: 600, background: cellBg, fontSize: FONT_SIZE, whiteSpace: "nowrap", verticalAlign: "middle", borderBottom: "2px solid #CFD8DC", position: "sticky", left: 0, zIndex: 2 }}>
                          {editMode ? (
                            <EditableCell
                              type="text"
                              value={row.commessa.codice}
                              onSave={async (v) => {
                                if (v.trim() && v.trim() !== row.commessa.codice) {
                                  try { await api.patchCommessa(row.commessa.id, { codice: v.trim() }); loadTabella(); }
                                  catch (e) { setError(e.message); }
                                }
                              }}
                              style={{ fontWeight: 600, fontSize: FONT_SIZE, color: disattivata ? DS.textSub : DS.text, textDecoration: disattivata ? "line-through" : undefined }}
                            />
                          ) : (
                            <span title={row.commessa.descrizione || ""} style={disattivata ? { color: DS.textSub, textDecoration: "line-through" } : undefined}>{row.commessa.codice}</span>
                          )}
                          {disattivata && <span style={{ fontSize: 10, color: DS.textSub, marginLeft: 6, fontWeight: 400 }}>esclusa</span>}
                        </td>
                      )}

                      {/* Controlli commessa (sticky): disattiva / archivia / elimina */}
                      {isFirst && (
                        <td rowSpan={maxCells} style={{ ...styles.td, background: cellBg, padding: 0, textAlign: "center", verticalAlign: "middle", borderBottom: "2px solid #CFD8DC", position: "sticky", left: MIN_COMMESSA, zIndex: 2, boxShadow: "3px 0 6px -2px rgba(0,0,0,0.18)" }}>
                          {editMode && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 3, alignItems: "center", padding: "2px 0" }}>
                              <button onClick={() => handleToggleContaCarico(row.commessa.id, row.commessa.conta_carico)} title={disattivata ? "Riattiva nel carico" : "Disattiva dal carico (simulazione)"} style={{ ...styles.delBtn, width: 24, height: 22, fontSize: 11, color: disattivata ? DS.success : DS.grey }}>{disattivata ? <Icon name="play" size={13} /> : <Icon name="pause" size={13} />}</button>
                              <button onClick={() => handleArchiviaCommessa(row)} title="Archivia commessa" style={{ ...styles.delBtn, width: 24, height: 22, fontSize: 11 }}><Icon name="archive" size={13} /></button>
                              <button onClick={() => handleDeleteCommessa(row.commessa.id, row.commessa.codice)} title="Elimina commessa" style={{ ...styles.delBtn, width: 24, height: 22, fontSize: 14, color: DS.error }}>×</button>
                            </div>
                          )}
                        </td>
                      )}

                      {/* Colonne settimane */}
                      {weeks.map(w => {
                        const cells    = row.weeks[String(w.id)] || [{}];
                        const collapsed = !!collapsedWeeks[w.id];
                        const borderB  = isLast ? "2px solid #CFD8DC" : `1px solid #ECEFF1`;

                        // Settimana collassata
                        if (collapsed) {
                          if (!isFirst) return <React.Fragment key={w.id} />;
                          const totNom = cells.reduce((s, c) => s + (c.colli_nominali      ?? 0), 0);
                          const totRes = cells.reduce((s, c) => s + (c.colli_residui       ?? 0), 0);
                          return (
                            <td key={w.id} rowSpan={maxCells} style={{ ...styles.td, background: COL_READ, textAlign: "center", padding: "4px 4px", borderLeft: WEEK_BORDER, verticalAlign: "middle", borderBottom: "2px solid #CFD8DC" }}>
                              <div style={{ fontSize: 11, color: "#BBBBBB", lineHeight: 1 }}>nom.</div>
                              <div style={{ fontSize: 22, fontWeight: 700, color: DS.text, lineHeight: 1.2 }}>{totNom.toLocaleString("it-IT")}</div>
                              <div style={{ marginTop: 4, fontSize: 11, color: "#BBBBBB", lineHeight: 1 }}>res.</div>
                              <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, color: totRes < 0 ? "#EF9A9A" : (totRes === 0 ? "#A5D6A7" : "#BBBBBB") }}>
                                {totRes.toLocaleString("it-IT")}
                              </div>
                            </td>
                          );
                        }

                        // Nessuna cella a questo sub-row per questa settimana
                        const cell = cells[subRow] || null;
                        const isLastSubRowForWeek = subRow === cells.length - 1;

                        if (!cell) {
                          return (
                            <React.Fragment key={w.id}>
                              <td style={{ ...styles.td, background: COL_INPUT, minWidth: MIN_CNT, borderLeft: WEEK_BORDER, borderBottom: borderB, position: "sticky", left: STICKY_CNT_LEFT, zIndex: 1, boxShadow: CNT_SHADOW }} />
                              <td style={{ ...styles.td, background: COL_READ, borderBottom: borderB }} />
                              {reparti.map(r => (
                                <td key={r.id} style={{ ...styles.td, background: COL_READ, borderBottom: borderB, borderLeft: "2px solid #B0BEC5" }} />
                              ))}
                            </React.Fragment>
                          );
                        }

                        const hoverKey = `${row.commessa.id}_${w.id}_${subRow}`;
                        const isHovered = hoveredAdd === hoverKey;

                        const menuOpen = spostaMenu
                          && spostaMenu.commessaId === row.commessa.id
                          && spostaMenu.container === cell.container_schedulato
                          && spostaMenu.fromWeekId === w.id;

                        return (
                          <React.Fragment key={w.id}>
                            {/* Container (sticky a sinistra) */}
                            <td
                              style={{ ...styles.td, background: COL_INPUT, minWidth: MIN_CNT, borderLeft: WEEK_BORDER, borderBottom: borderB, verticalAlign: "middle", position: "sticky", left: STICKY_CNT_LEFT, zIndex: menuOpen ? 30 : 1, boxShadow: CNT_SHADOW }}
                              onMouseEnter={() => setHoveredAdd(hoverKey)}
                              onMouseLeave={() => setHoveredAdd(null)}
                            >
                              <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 2 }}>
                                <EditableCell
                                  value={cell.container_schedulato || ""}
                                  onSave={v => handleContainerChange(row.commessa.id, w.id, subRow, v)}
                                  readOnly={!puoModificare}
                                />
                                {editMode && isHovered && cell.piano_id && (
                                  <button onClick={() => setSpostaMenu(menuOpen ? null : { commessaId: row.commessa.id, container: cell.container_schedulato, fromWeekId: w.id })} title="Sposta in un'altra settimana"
                                    style={{ ...styles.delBtn, width: 20, height: 20, fontSize: 13, flexShrink: 0, color: DS.grey, borderColor: DS.grey }}><Icon name="swap" size={13} /></button>
                                )}
                                {cells.length > 1 && isHovered && (
                                  <button onClick={() => handleDeleteContainer(row.commessa.id, w.id, subRow)} title="Rimuovi container"
                                    style={{ ...styles.delBtn, width: 20, height: 20, fontSize: 13, flexShrink: 0, color: DS.error, borderColor: "#EF9A9A" }}>×</button>
                                )}
                                {isLastSubRowForWeek && isHovered && (
                                  <button onClick={() => handleAddContainer(row.commessa.id, w.id)} title="Aggiungi container"
                                    style={{ ...styles.delBtn, width: 20, height: 20, fontSize: 14, flexShrink: 0, color: DS.blue, borderColor: DS.blue, fontWeight: 700 }}>+</button>
                                )}
                                {menuOpen && (
                                  <div style={styles.spostaMenu}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: DS.textSub, padding: "4px 10px", textTransform: "uppercase", letterSpacing: 0.5 }}>Sposta in…</div>
                                    {weeks.filter(tw => tw.id !== w.id).map(tw => (
                                      <button key={tw.id}
                                        onClick={() => handleSpostaContainer(row.commessa.id, cell.container_schedulato, w.id, tw.id)}
                                        style={styles.spostaMenuItem}>
                                        {tw.nome}
                                      </button>
                                    ))}
                                    {weeks.length <= 1 && (
                                      <div style={{ fontSize: 12, color: DS.textSub, padding: "6px 10px" }}>Nessun'altra settimana</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>

                            {/* Riepilogo: somma nominali + somma residui */}
                            <td style={{ ...styles.td, padding: 0, background: COL_READ, borderBottom: borderB, verticalAlign: "stretch" }}>
                              <RiepilogoCell nom={cell.colli_nominali ?? 0} res={cell.colli_residui ?? 0} />
                            </td>

                            {/* Colonne reparti */}
                            {reparti.map(rep => {
                              const repData = (cell.reparti || []).find(r => r.reparto_id === rep.id) || {
                                reparto_id: rep.id, reparto_nome: rep.nome,
                                colli_nominali: 0, colli_anticipati: 0,
                                colli_fabbisogno: 0, colli_versati: 0, colli_residui: 0,
                              };
                              return (
                                <td key={rep.id} style={{ ...styles.td, padding: 0, borderBottom: borderB, verticalAlign: "stretch", borderLeft: "2px solid #B0BEC5" }}>
                                  <RepCell
                                    rep={repData}
                                    disabled={!cell.piano_id || !puoModificare}
                                    onNomSave={v => handleNominaliChange(row.commessa.id, w.id, subRow, rep.id, v)}
                                  />
                                </td>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  );
                });
              })}

              {!soloTotali && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} style={{ ...styles.td, textAlign: "center", color: "#a0aec0", padding: 28, fontSize: FONT_SIZE }}>
                    Nessuna commessa. Aggiungine una con il pulsante "+ Commessa".
                  </td>
                </tr>
              )}

              {/* ── Riga totali per reparto ── */}
              <tr style={{ borderTop: "2px solid #CFD8DC" }}>
                <td colSpan={2} style={{ ...styles.tdTotal, background: COL_READ, fontWeight: 700, fontSize: FONT_LABEL, whiteSpace: "nowrap", position: "sticky", left: 0, zIndex: 2, boxShadow: "3px 0 6px -2px rgba(0,0,0,0.18)" }}>Totali</td>
                {weeks.map(w => {
                  const t = totali[String(w.id)] || {};
                  const collapsed = !!collapsedWeeks[w.id];
                  if (collapsed) {
                    const tNom = t.tot_nominali ?? 0;
                    const tRes = t.tot_residui  ?? 0;
                    return (
                      <td key={w.id} style={{ ...styles.tdTotal, background: COL_READ, textAlign: "center", borderLeft: WEEK_BORDER, padding: "4px 4px" }}>
                        <div style={{ fontSize: 11, color: "#BBBBBB", lineHeight: 1 }}>nom.</div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: DS.text, lineHeight: 1.2 }}>{tNom.toLocaleString("it-IT")}</div>
                        <div style={{ marginTop: 4, fontSize: 11, color: "#BBBBBB", lineHeight: 1 }}>res.</div>
                        <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, color: tRes < 0 ? "#EF9A9A" : (tRes === 0 ? "#A5D6A7" : "#BBBBBB") }}>
                          {tRes.toLocaleString("it-IT")}
                        </div>
                      </td>
                    );
                  }
                  const wt = weekRepTotals[w.id] || {};
                  const totNom = reparti.reduce((s, r) => s + (wt[r.id]?.nom || 0), 0);
                  const totRes = reparti.reduce((s, r) => s + (wt[r.id]?.res || 0), 0);
                  return (
                    <React.Fragment key={w.id}>
                      <td style={{ ...styles.tdTotal, background: COL_READ, borderLeft: WEEK_BORDER, position: "sticky", left: STICKY_CNT_LEFT, zIndex: 2, boxShadow: CNT_SHADOW }} />
                      <td style={{ ...styles.tdTotal, padding: 0, background: COL_READ }}>
                        <RiepilogoCell nom={totNom} res={totRes} />
                      </td>
                      {reparti.map(rep => {
                        const rt = wt[rep.id] || {};
                        return (
                          <td key={rep.id} style={{ ...styles.tdTotal, padding: 0, borderLeft: "2px solid #B0BEC5" }}>
                            <RepCell
                              rep={{ colli_nominali: rt.nom || 0, colli_anticipati: rt.ant || 0, colli_fabbisogno: rt.fab || 0, colli_versati: rt.ver || 0, colli_residui: rt.res || 0 }}
                              disabled
                            />
                          </td>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tr>

              {/* ── Numero persone ── */}
              <tr>
                <td colSpan={2} style={{ ...styles.tdTotal, background: COL_INPUT, fontWeight: 700, fontSize: FONT_LABEL, whiteSpace: "nowrap", position: "sticky", left: 0, zIndex: 2, boxShadow: "3px 0 6px -2px rgba(0,0,0,0.18)" }}>Numero persone</td>
                {weeks.map(w => {
                  const collapsed = !!collapsedWeeks[w.id];
                  const v = w.numero_persone ?? 0;
                  if (collapsed) return (
                    <td key={w.id} style={{ ...styles.tdTotal, background: COL_INPUT, fontWeight: 700, fontSize: FONT_TOTAL, textAlign: "center", borderLeft: WEEK_BORDER }}>
                      {v.toLocaleString("it-IT")}
                    </td>
                  );
                  return (
                    <React.Fragment key={w.id}>
                      <td style={{ ...styles.tdTotal, background: COL_INPUT, borderLeft: WEEK_BORDER, minWidth: MIN_CNT, position: "sticky", left: STICKY_CNT_LEFT, zIndex: 2, boxShadow: CNT_SHADOW }}>
                        <EditableCell type="number" value={v}
                          onSave={val => handleNumPersoneChange(w.id, val)}
                          readOnly={!puoModificare}
                          style={{ textAlign: "left", paddingLeft: 4, fontWeight: 700, fontSize: FONT_TOTAL }} />
                      </td>
                      <td colSpan={reparti.length + 1} style={{ ...styles.tdTotal, background: COL_READ }} />
                    </React.Fragment>
                  );
                })}
              </tr>

              {/* ── Colli/sett./pers. ── */}
              <tr>
                <td colSpan={2} style={{ ...styles.tdTotal, background: COL_INPUT, fontWeight: 700, fontSize: FONT_LABEL, whiteSpace: "nowrap", position: "sticky", left: 0, zIndex: 2, boxShadow: "3px 0 6px -2px rgba(0,0,0,0.18)" }}>Colli/sett./pers.</td>
                {weeks.map(w => {
                  const collapsed = !!collapsedWeeks[w.id];
                  const v = w.colli_per_settimana_persona ?? 235;
                  if (collapsed) return (
                    <td key={w.id} style={{ ...styles.tdTotal, background: COL_INPUT, fontWeight: 700, fontSize: FONT_TOTAL, textAlign: "center", borderLeft: WEEK_BORDER }}>
                      {v.toLocaleString("it-IT")}
                    </td>
                  );
                  return (
                    <React.Fragment key={w.id}>
                      <td style={{ ...styles.tdTotal, background: COL_INPUT, borderLeft: WEEK_BORDER, minWidth: MIN_CNT, position: "sticky", left: STICKY_CNT_LEFT, zIndex: 2, boxShadow: CNT_SHADOW }}>
                        <EditableCell type="number" value={v}
                          onSave={val => handleColliPpChange(w.id, val)}
                          readOnly={!puoModificare}
                          style={{ textAlign: "left", paddingLeft: 4, fontWeight: 700, fontSize: FONT_TOTAL }} />
                      </td>
                      <td colSpan={reparti.length + 1} style={{ ...styles.tdTotal, background: COL_READ }} />
                    </React.Fragment>
                  );
                })}
              </tr>

              {/* ── Giorni residui ── */}
              <tr>
                <td colSpan={2} style={{ ...styles.tdTotal, background: COL_INPUT, fontWeight: 700, fontSize: FONT_LABEL, whiteSpace: "nowrap", position: "sticky", left: 0, zIndex: 2, boxShadow: "3px 0 6px -2px rgba(0,0,0,0.18)" }}>Giorni residui</td>
                {weeks.map(w => {
                  const collapsed = !!collapsedWeeks[w.id];
                  const v = w.giorni_residui ?? 5;
                  if (collapsed) return (
                    <td key={w.id} style={{ ...styles.tdTotal, background: COL_INPUT, fontWeight: 700, fontSize: FONT_TOTAL, textAlign: "center", borderLeft: WEEK_BORDER }}>
                      {v.toLocaleString("it-IT")}
                    </td>
                  );
                  return (
                    <React.Fragment key={w.id}>
                      <td style={{ ...styles.tdTotal, background: COL_INPUT, borderLeft: WEEK_BORDER, minWidth: MIN_CNT, position: "sticky", left: STICKY_CNT_LEFT, zIndex: 2, boxShadow: CNT_SHADOW }}>
                        <EditableCell type="number" value={v}
                          onSave={val => handleGiorniResiduiChange(w.id, val)}
                          readOnly={!puoModificare}
                          style={{ textAlign: "left", paddingLeft: 4, fontWeight: 700, fontSize: FONT_TOTAL }} />
                      </td>
                      <td colSpan={reparti.length + 1} style={{ ...styles.tdTotal, background: COL_READ }} />
                    </React.Fragment>
                  );
                })}
              </tr>

              {/* ── Capacità installata (calcolata) ── */}
              <tr>
                <td colSpan={2} style={{ ...styles.tdTotal, background: COL_READ, fontWeight: 700, fontSize: FONT_LABEL, whiteSpace: "nowrap", position: "sticky", left: 0, zIndex: 2, boxShadow: "3px 0 6px -2px rgba(0,0,0,0.18)" }}>
                  Capacità installata
                  <div style={{ fontSize: 10, fontWeight: 400, color: DS.textSub }}>gg × pers × cpp / 5</div>
                </td>
                {weeks.map(w => {
                  const collapsed = !!collapsedWeeks[w.id];
                  const cpp = w.colli_per_settimana_persona ?? 235;
                  const cap = (w.giorni_residui || 0) * (w.numero_persone || 0) * (cpp / 5);
                  const capFmt = Math.round(cap).toLocaleString("it-IT");
                  if (collapsed) return (
                    <td key={w.id} style={{ ...styles.tdTotal, background: COL_READ, fontWeight: 700, fontSize: FONT_TOTAL, textAlign: "center", borderLeft: WEEK_BORDER }}>
                      {capFmt}
                    </td>
                  );
                  return (
                    <React.Fragment key={w.id}>
                      <td style={{ ...styles.tdTotal, background: COL_READ, borderLeft: WEEK_BORDER, fontWeight: 700, fontSize: FONT_TOTAL, paddingLeft: 10, position: "sticky", left: STICKY_CNT_LEFT, zIndex: 2, boxShadow: CNT_SHADOW }}>
                        {capFmt}
                      </td>
                      <td colSpan={reparti.length + 1} style={{ ...styles.tdTotal, background: COL_READ }} />
                    </React.Fragment>
                  );
                })}
              </tr>

              {/* ── Capacità residua (colli + teste) ── */}
              <tr>
                <td colSpan={2} style={{ ...styles.tdTotal, background: COL_READ, fontWeight: 700, fontSize: FONT_LABEL, whiteSpace: "nowrap", position: "sticky", left: 0, zIndex: 2, boxShadow: "3px 0 6px -2px rgba(0,0,0,0.18)" }}>Capacità residua</td>
                {weeks.map(w => {
                  const collapsed  = !!collapsedWeeks[w.id];
                  const cpp        = w.colli_per_settimana_persona ?? 235;
                  const cap        = (w.giorni_residui || 0) * (w.numero_persone || 0) * (cpp / 5);
                  const totRes     = reparti.reduce((s, r) => s + (weekRepTotals[w.id]?.[r.id]?.res || 0), 0);
                  const capRes     = cap - totRes;
                  const rateGg     = cpp > 0 && (w.giorni_residui || 0) > 0
                    ? (cpp / 5) * (w.giorni_residui || 0)
                    : 0;
                  const teste      = rateGg > 0 ? capRes / rateGg : 0;
                  const isNeg      = capRes < 0;
                  const color      = isNeg ? DS.error : DS.success;
                  const bg         = isNeg ? COL_VERDE_NEG : COL_READ;

                  const content = (
                    <div>
                      <span style={{ fontWeight: 700, fontSize: FONT_TOTAL, color }}>{Math.round(capRes).toLocaleString("it-IT")} colli</span>
                      {rateGg > 0 && (
                        <span style={{ fontSize: 13, color: DS.textSub, marginLeft: 8 }}>
                          ({teste.toFixed(1)} teste)
                        </span>
                      )}
                    </div>
                  );

                  if (collapsed) return (
                    <td key={w.id} style={{ ...styles.tdTotal, background: bg, textAlign: "center", borderLeft: WEEK_BORDER, padding: "4px 6px" }}>
                      <div style={{ fontSize: FONT_TOTAL, fontWeight: 700, color }}>{Math.round(capRes).toLocaleString("it-IT")}</div>
                      {rateGg > 0 && <div style={{ fontSize: 11, color: DS.textSub }}>{teste.toFixed(1)} t.</div>}
                    </td>
                  );
                  return (
                    <React.Fragment key={w.id}>
                      <td style={{ ...styles.tdTotal, background: bg, borderLeft: WEEK_BORDER, paddingLeft: 10, position: "sticky", left: STICKY_CNT_LEFT, zIndex: 2, boxShadow: CNT_SHADOW }}>
                        {content}
                      </td>
                      <td colSpan={reparti.length + 1} style={{ ...styles.tdTotal, background: bg }} />
                    </React.Fragment>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Legenda */}
        <div style={{ display: "flex", gap: 20, marginTop: 14, flexWrap: "wrap", padding: "10px 14px", background: "#FAFAFA", border: `1px solid ${DS.border}`, borderRadius: 8 }}>
          {[
            { bg: COL_INPUT, label: "Campo editabile" },
            { bg: COL_READ,  label: "Valore calcolato / automatico" },
          ].map(({ bg, label }) => (
            <span key={label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: DS.textSub }}>
              <span style={{ display: "inline-block", width: 14, height: 14, background: bg, border: `1px solid ${DS.border}`, borderRadius: 3 }} />
              {label}
            </span>
          ))}
        </div>
      </div>

      {showModaleWeek     && <ModaleWeek     onClose={() => setShowModaleWeek(false)}     onSave={handleAddWeek}     weeks={weeks} />}
      {showModaleCommessa && <ModaleCommessa onClose={() => setShowModaleCommessa(false)} onSave={handleAddCommessa} />}
      {modaleStampa && <ModaleStampa mode={modaleStampa} weeks={weeks} onClose={() => setModaleStampa(null)} onConfirm={handleConfirmStampa} />}
    </div>
  );
}

// ── Stili ──────────────────────────────────────────────────────────────────────
const styles = {
  table:       { borderCollapse: "collapse", fontSize: FONT_SIZE, boxShadow: "0 2px 8px rgba(0,0,0,0.1)", background: "#fff", fontFamily: FONT },
  th:          { border: `1px solid #CFD8DC`, padding: "9px 6px", fontWeight: 700, whiteSpace: "nowrap", textAlign: "center", userSelect: "none", letterSpacing: 0.2 },
  td:          { borderTop: `1px solid #CFD8DC`, borderBottom: `1px solid #CFD8DC`, borderLeft: `1px solid #ECEFF1`, borderRight: `1px solid #ECEFF1`, padding: "4px 6px", verticalAlign: "middle" },
  tdTotal:     { border: `1px solid #CFD8DC`, padding: "7px 8px", verticalAlign: "middle", fontSize: FONT_SIZE },
  delBtn:      { background: "none", border: `1px solid #CFD8DC`, borderRadius: 4, width: 28, height: 28, cursor: "pointer", fontSize: 16, color: DS.grey, display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0 },
  spostaMenu:  { position: "absolute", top: "100%", left: 0, marginTop: 2, background: "#fff", border: `1px solid ${DS.border}`, borderRadius: 6, boxShadow: "0 4px 16px rgba(0,0,0,0.18)", zIndex: 20, minWidth: 140, maxHeight: 260, overflowY: "auto", padding: "4px 0" },
  spostaMenuItem: { display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "7px 12px", cursor: "pointer", fontSize: 14, color: DS.text, fontFamily: FONT, whiteSpace: "nowrap" },
  arrowBtn:    { background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 3, width: 22, height: 18, cursor: "pointer", fontSize: 9, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, lineHeight: 1 },
  iconBtn:     { background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 3, width: 22, height: 18, cursor: "pointer", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", padding: 0, lineHeight: 1 },
  loading:     { padding: 60, textAlign: "center", color: DS.textSub, fontSize: FONT_SIZE, fontFamily: FONT },
  errorBanner: { background: "#FFEBEE", border: "1px solid #EF9A9A", color: DS.error, padding: "10px 14px", borderRadius: 6, marginBottom: 12, fontSize: FONT_SIZE, display: "flex", alignItems: "center", fontWeight: 600 },
  overlay:     { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modale:      { background: "#fff", borderRadius: 10, padding: 28, minWidth: 360, boxShadow: "0 8px 32px rgba(0,0,0,0.25)", display: "flex", flexDirection: "column", gap: 12, fontFamily: FONT },
  label:       { fontSize: 12, fontWeight: 700, color: DS.textSub, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 2 },
  input:       { border: `1px solid ${DS.border}`, borderRadius: 6, padding: "10px 12px", fontSize: FONT_SIZE, width: "100%", boxSizing: "border-box", outline: "none", fontFamily: FONT, color: DS.text },
  btnPrimary:  { background: DS.blue, color: "#fff", border: "none", borderRadius: 6, padding: "10px 22px", cursor: "pointer", fontWeight: 700, fontSize: FONT_SIZE, fontFamily: FONT },
  btnSecondary:{ background: DS.light, color: DS.text, border: `1px solid ${DS.border}`, borderRadius: 6, padding: "10px 22px", cursor: "pointer", fontWeight: 600, fontSize: FONT_SIZE, fontFamily: FONT },
};
